import React from 'react'
import { Link } from 'react-router-dom'
import { Room } from '../types/room'
import { useTranslation } from '../hooks/use-i18n'
import { getChessTournamentConfig } from '../constants/chessTournamentTypes'

interface RoomCardProps {
  room: Room
}

const RoomCard: React.FC<RoomCardProps> = ({ room }) => {
  const { t } = useTranslation()
  const gameBadgeClass =
    room.gameType === 'football'
      ? 'bg-warning/20 text-warning'
      : room.gameType === 'chess'
      ? 'bg-muted text-foreground'
      : room.gameType === 'golf'
      ? 'bg-emerald-500/20 text-emerald-300'
      : 'bg-destructive/20 text-destructive'
  const typeBadgeClass = room.type === 'company' ? 'bg-warning/20 text-warning' : 'bg-success/20 text-success'
  const typeLabel = room.type === 'company' ? t('room.typeCompany') : t('room.typeIndividual')
  const gameLabel =
    room.gameType === 'football'
      ? `🏆 ${t('room.gameFootball')}`
      : room.gameType === 'chess'
      ? `♟️ ${t('room.gameChess')}`
      : room.gameType === 'golf'
      ? '⛳ Golf'
      : `🏎️ ${t('room.gameFormula1')}`
  const chessTournamentConfig = room.gameType === 'chess' && room.chessTournamentType && room.chessTournamentType !== 'none'
    ? getChessTournamentConfig(room.chessTournamentType)
    : null
  const chessTournamentTypeLabel = room.gameType === 'chess'
    ? (chessTournamentConfig
        ? `${chessTournamentConfig.icon} ${t(`chessTypes.${chessTournamentConfig.keyPrefix}Name`)}`
        : `— ${t('createRoom.tournamentNone')}`)
    : null

  return (
    <div className="glass-card rounded-xl sm:rounded-2xl p-3 sm:p-5 hover:bg-muted/10 transition-all group h-full">
      <div className="flex justify-between items-start mb-2.5 sm:mb-4 gap-2">
        <h3 className="font-semibold text-lg sm:text-display-2 group-hover:text-primary transition-colors text-foreground truncate flex-1">{room.name}</h3>
        <div className="flex flex-col items-end gap-1 sm:gap-1.5 shrink-0">
          <span className={`text-[10px] font-bold uppercase px-1.5 sm:px-2 py-0.5 rounded-full ${typeBadgeClass}`}>
            {typeLabel}
          </span>
          {room.gameType && (
            <span className={`text-[10px] font-medium px-1.5 sm:px-2 py-0.5 rounded-full ${gameBadgeClass}`}>
              {gameLabel}
            </span>
          )}
          {chessTournamentTypeLabel && (
            <span
              className={`text-[11px] sm:text-xs font-medium px-2 sm:px-2.5 py-1 rounded-full max-w-[9rem] truncate inline-block ${
                chessTournamentConfig
                  ? `bg-gradient-to-r ${chessTournamentConfig.gradient} text-white/95`
                  : 'bg-muted/60 text-muted-foreground'
              }`}
              title={chessTournamentConfig ? t(`chessTypes.${chessTournamentConfig.keyPrefix}Name`) : t('createRoom.tournamentNone')}
            >
              {chessTournamentTypeLabel}
            </span>
          )}
        </div>
      </div>

      {room.gameType === 'football' && room.leagues && room.leagues.length > 0 && (
        <div className="mb-2 sm:mb-4">
          <p className="text-small sm:text-body-lg text-muted-foreground mb-1 sm:mb-2 font-medium">{t('room.leagues')}:</p>
          <div className="flex flex-wrap gap-1 sm:gap-2">
            {room.leagues.map((league) => (
              <span key={league} className="bg-muted/50 text-foreground px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-small font-medium">
                {league.replace('-', ' ').toUpperCase()}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="mb-2.5 sm:mb-4 space-y-1 sm:space-y-1.5">
        <div className="flex items-center justify-between text-small sm:text-body-lg">
          <span className="text-muted-foreground">{t('room.players')}:</span>
          <span className="text-foreground font-semibold text-small sm:text-nav">{room.players.length}</span>
        </div>
        <div className="flex items-center justify-between text-small sm:text-body-lg">
          <span className="text-muted-foreground">{t('room.owner')}:</span>
          <span className="text-foreground font-semibold text-small sm:text-nav truncate ml-2">{room.owner.name}</span>
        </div>
      </div>

      <Link
        to={
          room.gameType === 'chess'
            ? `/chess/${room._id}`
            : room.gameType === 'golf'
            ? `/golf/${room._id}`
            : room.gameType === 'formula1'
            ? `/formula1/${room._id}`
            : `/room/${room._id}`
        }
        className="block w-full text-center py-2 sm:py-2.5 rounded-lg sm:rounded-xl bg-primary text-primary-foreground text-small sm:text-nav font-semibold hover:opacity-90 transition-all"
      >
        {t('room.enterRoom')}
      </Link>
    </div>
  )
}

export default RoomCard
